import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { BarcodeScannerDialog } from '../../common/components';
import type { ScanResult } from '../../common/components';
import { extractErrorMessage } from '../../../utils/errorUtils';
import { formatDateTime } from '../../../utils/dateTimeUtils';
import type {
  CoverageLine,
  ScanOutcome,
} from '../../../types/stockAudit';
import AuditResultTabs from './AuditResultTabs';
import ConfirmActionDialog from './ConfirmActionDialog';
import { SCAN_RESULT_LABELS } from './scanResultLabels';
import { useStockAuditDetail } from './useStockAuditDetail';

const toScanFeedback = (outcome: ScanOutcome): ScanResult => {
  const serial = outcome.serialNumber;
  const product = outcome.productName ? ` (${outcome.productName})` : '';
  const note = outcome.note ? `. ${outcome.note}` : '';

  if (outcome.result === 'FOUND') {
    return { ok: true, message: `Знайдено: ${serial}${product}` };
  }
  return {
    ok: false,
    message: `${SCAN_RESULT_LABELS[outcome.result]}: ${serial}${product}${note}`,
  };
};

const hasUnserializedUnits = (line: CoverageLine): boolean =>
  line.stockBalance > line.serialCount;

export default function StockAuditDetailPage() {
  const { id, auditId } = useParams<{ id: string; auditId: string }>();
  const navigate = useNavigate();

  if (!id || !auditId) {
    throw new Error('Company ID and audit ID are required');
  }

  const companyId = parseInt(id);
  const { data, actions } = useStockAuditDetail(parseInt(auditId));

  // Scanning
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualSerial, setManualSerial] = useState('');
  const [manualBusy, setManualBusy] = useState(false);
  const [manualFeedback, setManualFeedback] = useState<ScanResult | null>(null);

  // Confirmations
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const goToList = () => {
    navigate(`/companies/${companyId}/stock-audits`);
  };

  const scanSerial = async (serialNumber: string): Promise<ScanResult> => {
    try {
      return toScanFeedback(await actions.scan(serialNumber));
    } catch (err) {
      console.error('Failed to scan serial number:', err);
      return {
        ok: false,
        message: extractErrorMessage(err, `Не вдалося обробити: ${serialNumber}`),
      };
    }
  };

  const handleManualAdd = async () => {
    const serialNumber = manualSerial.trim();
    if (!serialNumber) {
      return;
    }

    setManualBusy(true);
    setManualFeedback(await scanSerial(serialNumber));
    setManualSerial('');
    setManualBusy(false);
  };

  const handleManualKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !manualBusy) {
      handleManualAdd();
    }
  };

  const handleComplete = async () => {
    await actions.complete();
    setConfirmComplete(false);
  };

  const handleCancel = async () => {
    const cancelled = await actions.cancel();
    if (cancelled) {
      goToList();
      return;
    }
    setConfirmCancel(false);
  };

  const { details, loading, error } = data;

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error || !details) {
      return <Alert severity="error">{error ?? 'Ревізію не знайдено'}</Alert>;
    }

    const { audit } = details;
    const isOpen = audit.status === 'IN_PROGRESS';
    const uncoveredLines = details.coverage.filter(hasUnserializedUnits);

    return (
      <>
        {/* Info */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="h6">{audit.stockName ?? `Склад ${audit.stockId}`}</Typography>
            <Chip
              label={isOpen ? 'Триває' : 'Закрито'}
              color={isOpen ? 'warning' : 'success'}
              size="small"
            />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {audit.productIds.length === 0
              ? 'Весь склад'
              : `Фільтр за продуктами: ${audit.productIds.length}`}
            {' · '}
            Початок: {formatDateTime(audit.createdDate)}
            {audit.completedDate && ` · Закрито: ${formatDateTime(audit.completedDate)}`}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip
              label={`Очікується: ${details.expectedCount}`}
              variant="outlined"
            />
            <Chip
              label={`Знайдено: ${details.foundCount}`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Відсутні: ${details.missingCount}`}
              color={details.missingCount > 0 ? 'warning' : 'default'}
              variant="outlined"
            />
            <Chip
              label={`Зайві: ${details.unexpectedCount}`}
              color={details.unexpectedCount > 0 ? 'error' : 'default'}
              variant="outlined"
            />
          </Box>
        </Paper>

        {actions.actionError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={actions.clearActionError}
          >
            {actions.actionError}
          </Alert>
        )}

        {/* Scanning */}
        {isOpen && (
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<PhotoCameraIcon />}
              onClick={() => setScannerOpen(true)}
              sx={{ mb: 2 }}
            >
              Сканувати камерою
            </Button>
            <TextField
              label="Серійний номер вручну"
              value={manualSerial}
              onChange={(event) => setManualSerial(event.target.value)}
              onKeyDown={handleManualKeyDown}
              disabled={manualBusy}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={handleManualAdd}
                        disabled={!manualSerial.trim() || manualBusy}
                        size="small"
                      >
                        Додати
                      </Button>
                    </InputAdornment>
                  ),
                },
              }}
            />
            {manualFeedback && (
              <Alert
                severity={manualFeedback.ok ? 'success' : 'warning'}
                sx={{ mt: 2 }}
                onClose={() => setManualFeedback(null)}
              >
                {manualFeedback.message}
              </Alert>
            )}
          </Paper>
        )}

        {isOpen && uncoveredLines.length > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
          >
            Без серійних номерів — ревізія цього не перевіряє:{' '}
            {uncoveredLines
              .map((line) => `${line.productName ?? line.productId} — ${line.stockBalance - line.serialCount} шт.`)
              .join('; ')}
          </Alert>
        )}

        {/* Results */}
        <AuditResultTabs
          missing={details.missing}
          scans={details.scans}
          canEdit={isOpen}
          onRemoveScan={actions.removeScan}
        />

        {/* Footer */}
        {isOpen && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Button
              color="error"
              onClick={() => setConfirmCancel(true)}
              disabled={actions.busy}
            >
              Скасувати ревізію
            </Button>
            <Button
              variant="contained"
              onClick={() => setConfirmComplete(true)}
              disabled={actions.busy}
            >
              Закрити ревізію
            </Button>
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={goToList}
          size="small"
        >
          До ревізій
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Ревізія
        </Typography>
      </Box>

      {renderBody()}

      <BarcodeScannerDialog
        open={scannerOpen}
        mode="continuous"
        onClose={() => setScannerOpen(false)}
        onDetected={scanSerial}
      />

      <ConfirmActionDialog
        open={confirmComplete}
        title="Закрити ревізію?"
        text="Після закриття ревізію не можна відкрити знову: відсутні одиниці буде зафіксовано. Щоб перевірити склад ще раз, створіть нову ревізію."
        confirmLabel="Закрити"
        loading={actions.busy}
        onConfirm={handleComplete}
        onClose={() => setConfirmComplete(false)}
      />

      <ConfirmActionDialog
        open={confirmCancel}
        title="Скасувати ревізію?"
        text="Усі скани цієї ревізії буде втрачено, вона не потрапить в історію."
        confirmLabel="Скасувати ревізію"
        confirmColor="error"
        loading={actions.busy}
        onConfirm={handleCancel}
        onClose={() => setConfirmCancel(false)}
      />
    </Box>
  );
}
